import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Progress,
  Skeleton
} from "./UI";
import { Crown, CreditCard, ArrowUpRight, Sparkles, Loader2 } from "lucide-react";
import { SubscriptionAPI } from "api/dashboard/service";
import { useAlert } from "components/UI_components/Alert";
import { useNavigate } from "react-router-dom";

const dummyInvoices = [
  { id: "INV-2024-012", date: "Dec 1, 2024", amount: "₹24,999", status: "paid" },
  { id: "INV-2024-011", date: "Nov 1, 2024", amount: "₹24,999", status: "paid" },
  { id: "INV-2024-010", date: "Oct 1, 2024", amount: "₹24,999", status: "paid" },
];

export function BillingSection() {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addAlert } = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const org = JSON.parse(localStorage.getItem("organization") || "{}");
      if (!org.id) {
        addAlert("Organization not found", "error");
        return;
      }

      const [subRes, plansRes] = await Promise.all([
        SubscriptionAPI.checkUserSubscription(org.id),
        SubscriptionAPI.getAllPlans()
      ]);

      if (subRes.status) {
        setSubscription(subRes.data);
      }
      else if (subRes.message === "User does not have an active subscription") {
        console.log("User does not have an active subscription");
        setSubscription(null);
      }
      if (plansRes.status) {
        setPlans(plansRes.data);
      }
    } catch (err) {
      console.error("Error fetching billing data:", err);
      // addAlert("Failed to load billing information", "error");
    } finally {
      setLoading(false);
    }
  };

  const currentPlanDetails = plans.find(p => p.id === subscription?.subscription_plan_id);

  const getStatusBadge = () => {
    if (!subscription) return <Badge variant="outline">Inactive</Badge>;
    if (subscription.active_flag) return <Badge className="bg-white/20 text-white border-0 hover:bg-white/30">Active</Badge>;
    if (subscription.razorpay_subscription) return <Badge className="bg-yellow-500/20 text-white border-0">Payment Pending</Badge>;
    return <Badge variant="destructive">Inactive</Badge>;
  };

  const calculateDaysLeft = (dateString) => {
    if (!dateString) return null;
    const diff = new Date(dateString) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const handleUpgrade = () => {
    navigate("/subscription");
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 w-1/3" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Billing & Plan</h2>
        <p className="text-text-gray mt-1">Manage your subscription and payment details.</p>
      </div>
      {
        subscription?.subscription_name ? (

          <Card className="border-sm-border/50 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-sm-primary via-sm-secondary to-sm-accent p-6 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="h-5 w-5" />
                    <span className="text-sm font-medium opacity-90">Current Plan</span>
                  </div>
                  <h3 className="text-2xl font-bold">{subscription?.subscription_name || "Free Trial"}</h3>
                  <p className="text-sm opacity-90 mt-1">
                    {currentPlanDetails ? currentPlanDetails.subscription_name : "Start exploring with our trial features"}
                  </p>
                </div>
                {getStatusBadge()}
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-1">
                  <p className="text-sm text-text-gray">Plan Cost</p>
                  <p className="text-xl font-semibold text-primary-text">
                    {currentPlanDetails?.total_charge ? `₹${currentPlanDetails.total_charge.toLocaleString("en-IN")}` : "₹0"}
                  </p>
                  <p className="text-xs text-text-gray">
                    {currentPlanDetails?.subscription_name?.toLowerCase().includes("yearly") ? "billed annually" : "per plan"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-text-gray">Next Renewal</p>
                  <p className="text-xl font-semibold text-primary-text">{formatDate(subscription?.valid_till)}</p>
                  {subscription?.valid_till && (
                    <p className="text-xs text-text-gray">
                      {calculateDaysLeft(subscription.valid_till)} days left
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-text-gray">Usage Statistics</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span>Team Members</span>
                      <span className="font-medium">{subscription?.active_members || 0} active</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Active Projects</span>
                      <span className="font-medium">{subscription?.active_projects || 0} active</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          // No Active Subscription Card
          <Card className="shadow-sm border-sm-primary/20 bg-sm-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-sm-primary/10">
                  <Sparkles className="h-6 w-6 text-sm-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-primary-text">Want more power?</h4>
                  <p className="text-sm text-text-gray">Upgrade your plan to unlock more members, unlimited projects and priority support.</p>
                </div>
                <Button className="gap-2" onClick={handleUpgrade}>
                  View All Plans
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-sm-primary" />
            Subscription Info
          </CardTitle>
          <CardDescription>Details about your current subscription engine.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 rounded-lg border border-sm-border bg-gray-bg/30">
              <div className="space-y-1">
                <p className="text-xs text-text-gray uppercase tracking-wider">Subscription ID</p>
                <p className="font-medium text-primary-text">{subscription?.razorpay_subscription || "N/A"}</p>
              </div>
              {subscription?.auto_renewable && (
                <Badge variant="outline" className="border-sm-primary text-sm-primary">Auto-renewal On</Badge>
              )}
            </div>
            {subscription?.razorpay_payment_link && !subscription.active_flag && (
              <Button
                variant="outline"
                className="w-full border-sm-primary text-sm-primary hover:bg-sm-primary hover:text-white"
                onClick={() => window.open(subscription.razorpay_payment_link, "_blank")}
              >
                Complete Pending Payment
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

